import { useTranslation } from 'react-i18next'
import LinkIcon from '~/assets/icons/link.svg?react'
import CompassIcon from '~/assets/compass.svg?react'
import PitchIcon from '~/assets/icons/pitch.svg?react'

import {
  useFloating,
  useInteractions,
  useClick,
  useHover,
  useRole,
  useDismiss,
  FloatingFocusManager,
  offset,
} from '@floating-ui/react'
import { useState } from 'react'
import { urlFromState } from '~/util/trackedURLParams'
import { useAppStore } from '~/hooks'
import { track } from '~/analytics'
import { Text } from '~/components/typography'
import { MapTooltipMachineContext } from '~/contexts/MapTooltipMachineContext'

type TooltipState = {
  isOpen: boolean
  content: string
  type: 'click' | 'hover'
  referenceElement: HTMLElement | null
}

export default function IconMenu() {
  const { t } = useTranslation()
  const state = useAppStore()
  const { context: mapTooltipContext } = MapTooltipMachineContext.useSelector((s) => s)
  const [tooltip, setTooltip] = useState<TooltipState>({
    isOpen: false,
    content: '',
    type: 'hover',
    referenceElement: null,
  })

  const { refs, floatingStyles, context } = useFloating({
    open: tooltip.isOpen,
    onOpenChange: (open) => setTooltip((prev) => ({ ...prev, isOpen: open })),
    placement: 'left-start',
    middleware: [offset(15)],
    elements: {
      reference: tooltip.referenceElement,
    },
  })
  const { mapRef } = useAppStore()

  const click = useClick(context)
  const dismiss = useDismiss(context)
  const role = useRole(context)
  const hover = useHover(context)

  const { getFloatingProps } = useInteractions([click, dismiss, role, hover])

  const copyUrl = (element: HTMLElement) => {
    const url = urlFromState(state, mapTooltipContext)
    console.log(url)
    navigator.clipboard.writeText(url)
    setTooltip({
      isOpen: true,
      content: t('tooltips.copiedUrl'),
      type: 'click',
      referenceElement: element,
    })
    track('share_link_copied')
    setTimeout(() => setTooltip((prev) => ({ ...prev, isOpen: false })), 2000)
  }

  function updatePitchAndBearing({ pitch = 0, bearing = 0 } = {}) {
    mapRef?.easeTo({
      pitch,
      bearing,
    })
  }

  const showTooltip = (content: string, element: HTMLElement) => {
    setTooltip({
      isOpen: true,
      content,
      type: 'hover',
      referenceElement: element,
    })
  }

  const hideTooltip = () => {
    setTooltip((prev) => ({ ...prev, isOpen: false }))
  }

  const isAngled = state.viewState.pitch !== 0 || state.viewState.bearing !== 0

  return (
    <div className="absolute right-4 top-5 z-50 hidden flex-col gap-2 rounded-lg bg-[#00000000] px-2 py-1 pr-0 backdrop-blur-[1px] sm:flex">
      <button
        type="button"
        onClick={(e) => copyUrl(e.currentTarget)}
        onMouseEnter={(e) => showTooltip(t('tooltips.linkToCurrent'), e.currentTarget)}
        onMouseLeave={hideTooltip}
      >
        <LinkIcon className="drop-shadow-[0_1px_4px_rgba(0,0,0,0.9)]" />
      </button>

      <button
        type="button"
        onClick={() => updatePitchAndBearing()}
        onMouseEnter={(e) => showTooltip(t('tooltips.alignViewport'), e.currentTarget)}
        onMouseLeave={hideTooltip}
      >
        <CompassIcon className="size-[42px] h-[32px] pr-4 drop-shadow-[0_1px_5px_rgba(0,0,0,0.9)]" />
      </button>

      <button
        type="button"
        onClick={() =>
          updatePitchAndBearing(isAngled ? { pitch: 0, bearing: 0 } : { pitch: 60, bearing: -45 })
        }
        onMouseEnter={(e) =>
          showTooltip(
            isAngled ? t('tooltips.removeAngleView') : t('tooltips.angleView'),
            e.currentTarget
          )
        }
        onMouseLeave={hideTooltip}
      >
        <PitchIcon
          className={`drop-shadow-[0_1px_3px_rgba(0,0,0,0.75)] ${isAngled ? 'text-blue-100' : 'text-white'}`}
        />
      </button>

      {tooltip.isOpen && (
        <FloatingFocusManager context={context}>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="min-w-32 rounded bg-[#EDEDED] px-3 py-1 focus:outline-none"
          >
            <Text className="text-neutral-midfi-grey" level={3}>
              {tooltip.content}
            </Text>
          </div>
        </FloatingFocusManager>
      )}
    </div>
  )
}
